import { competitorSupplierLabel } from '@/lib/hotel-competitor';
import { computeGrossPriceSummary, formatGrossPrice } from '@/lib/hotel-tax';
import type { HotelWithSelection } from '@/types/hotel-search';

function escapeHtml(text: string): string {
  return text
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');
}

function formatBookingPrice(hotel: HotelWithSelection, nights?: number): string {
  const currency = hotel.price_summary?.currency ?? 'BRL';
  const gross = hotel.rooms?.length
    ? computeGrossPriceSummary(hotel.rooms, currency)
    : null;

  if (!gross) return 'Preço indisponível';

  const range = Math.round(gross.min) !== Math.round(gross.max)
    ? `${formatGrossPrice(gross.min)} — ${formatGrossPrice(gross.max)}`
    : formatGrossPrice(gross.min);

  const nightsSuffix = nights && nights > 0 ? ` · ${nights} noites` : '';
  return `${currency} ${range} total${nightsSuffix}`;
}

function formatBookingRating(hotel: HotelWithSelection): string {
  const rating = hotel.rating;
  if (!rating?.score) return 'Rating indisponível';

  const parts = [`${rating.score.toFixed(1)}`];
  if (rating.label) parts.push(rating.label);
  if (rating.review_count) {
    parts.push(`(${rating.review_count.toLocaleString('pt-BR')} avaliações)`);
  }
  return parts.join(' · ');
}

/** HTML para InfoWindow ao passar o mouse no pin do mapa. */
export function buildHotelMapHoverHtml(hotel: HotelWithSelection, nights?: number): string {
  const name = escapeHtml(hotel.name);
  const bookingRating = escapeHtml(formatBookingRating(hotel));
  const bookingPrice = escapeHtml(formatBookingPrice(hotel, nights));

  const editorialLine = hotel.editorial_pick
    ? `<div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
        <span style="background: #f59e0b; color: white; padding: 2px 7px; border-radius: 4px; font-size: 11px; font-weight: 700;">★ Editorial</span>
        <span style="font-size: 12px; color: #92400e;">${escapeHtml(hotel.editorial_pick_source?.trim() || 'Lista ou blog')}</span>
      </div>`
    : '';

  const quotes = (hotel.selection.competitor_quote ?? []).filter(cq => {
    const priceT = cq.price?.trim() ?? '';
    const descT = cq.description?.trim() ?? '';
    return !!(cq.supplier || priceT || descT);
  });

  const supplierRows = quotes.map(cq => {
    const label = escapeHtml(cq.supplier ? competitorSupplierLabel(cq.supplier) : 'Fornecedor');
    const priceT = cq.price?.trim() ?? '';
    const descT = cq.description?.trim() ?? '';
    const supplierUrl = cq.supplier_url?.trim() ?? '';
    const detail = escapeHtml(priceT || descT || '—');
    const detailHtml = supplierUrl
      ? `<a href="${escapeHtml(supplierUrl)}" target="_blank" rel="noopener noreferrer" style="color: #451a03; text-decoration: underline;">${detail}</a>`
      : `<span style="color: #451a03; text-align: right; word-break: break-word;">${detail}</span>`;
    return `
      <div style="display: flex; justify-content: space-between; gap: 12px; font-size: 12px; line-height: 1.4;">
        <span style="color: #92400e; font-weight: 600; white-space: nowrap;">${label}</span>
        ${detailHtml}
      </div>`;
  }).join('');

  return `
    <div style="font-family: 'Sora', sans-serif; min-width: 220px; max-width: 280px; padding: 2px 0;">
      <div style="font-weight: 600; font-size: 14px; color: #111; margin-bottom: 6px; line-height: 1.3;">${name}</div>
      ${editorialLine}
      <div style="display: flex; align-items: center; gap: 6px; margin-bottom: 8px;">
        <span style="background: #007C82; color: white; padding: 2px 7px; border-radius: 4px; font-size: 11px; font-weight: 700;">Booking</span>
        <span style="font-size: 12px; color: #444;">${bookingRating}</span>
      </div>
      <div style="border-top: 1px solid #e5e7eb; padding-top: 8px; display: flex; flex-direction: column; gap: 6px;">
        <div style="display: flex; justify-content: space-between; gap: 12px; font-size: 12px; line-height: 1.4;">
          <span style="color: #007C82; font-weight: 600; white-space: nowrap;">Booking</span>
          <span style="color: #111; font-weight: 600; text-align: right; word-break: break-word;">${bookingPrice}</span>
        </div>
        ${supplierRows}
      </div>
    </div>
  `;
}
